import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  collection, 
  query, 
  onSnapshot,
  updateDoc,
  doc,
  where,
  addDoc,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { motion } from 'framer-motion';

const DriverDashboard = () => {
  const { currentUser, userData } = useAuth();
  const [availableBookings, setAvailableBookings] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('available');
  const [driverStats, setDriverStats] = useState({
    totalApplications: 0,
    acceptedJobs: 0,
    pendingApplications: 0
  });
  const [loading, setLoading] = useState(true);
  const [applyingBookingId, setApplyingBookingId] = useState(null);

  // Debug function to check driver data
  useEffect(() => {
    console.log('Driver Data:', {
      currentUser: currentUser?.uid,
      userData: userData,
      driverCity: userData?.city,
      userType: userData?.userType
    });
  }, [currentUser, userData]);

  useEffect(() => {
    if (!currentUser || !userData?.city || userData?.userType !== 'driver') {
      console.log('Driver not ready:', { 
        hasUser: !!currentUser, 
        hasCity: !!userData?.city, 
        isDriver: userData?.userType === 'driver' 
      });
      setAvailableBookings([]);
      setLoading(false);
      return;
    }

    console.log('Setting up bookings listener for city:', userData.city);

    // Fetch approved bookings in driver's city
    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('status', '==', 'approved'),
      where('city', '==', userData.city)
    );
    
    const unsubscribe = onSnapshot(bookingsQuery, 
      (snapshot) => {
        const bookingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Available bookings found:', bookingsData.length);
        console.log('Bookings details:', bookingsData);
        
        setAvailableBookings(bookingsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching available bookings:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          driverCity: userData?.city
        });
        setLoading(false);
        
        if (error.code === 'permission-denied') {
          console.log('Permission denied for bookings - check Firestore rules');
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser, userData]);

  useEffect(() => {
    if (!currentUser) return;

    // Fetch driver's applications
    const applicationsQuery = query(
      collection(db, 'applications'),
      where('driverId', '==', currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(applicationsQuery, 
      (snapshot) => {
        const applicationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setMyApplications(applicationsData);
        
        // Update stats
        setDriverStats({
          totalApplications: applicationsData.length,
          acceptedJobs: applicationsData.filter(app => app.status === 'accepted').length,
          pendingApplications: applicationsData.filter(app => app.status === 'pending').length
        });
      },
      (error) => {
        console.error('Error fetching applications:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Check if driver has already applied to a booking
  const hasAlreadyApplied = (bookingId) => {
    return myApplications.some(app => 
      app.bookingId === bookingId && 
      (app.status === 'pending' || app.status === 'accepted')
    );
  };

  const handleApplyForBooking = async (booking) => {
    // Check if already applied
    if (hasAlreadyApplied(booking.id)) {
      alert('You have already applied for this booking!');
      return;
    }

    // Check if currently applying
    if (applyingBookingId === booking.id) {
      return;
    }

    setApplyingBookingId(booking.id);

    try {
      if (!currentUser || !userData) {
        alert('Please log in as a driver to apply for bookings.');
        setApplyingBookingId(null);
        return;
      }

      console.log('Applying for booking:', booking.id);

      // First, check if application already exists
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('bookingId', '==', booking.id),
        where('driverId', '==', currentUser.uid)
      );
      
      const existingApps = await getDocs(applicationsQuery);
      if (!existingApps.empty) {
        alert('You have already applied for this booking!');
        setApplyingBookingId(null);
        return;
      }

      // Create application
      const applicationData = {
        bookingId: booking.id,
        driverId: currentUser.uid,
        driverName: `${userData.firstName} ${userData.lastName}`,
        driverPhone: userData.phone,
        driverEmail: currentUser.email,
        driverCity: userData.city,
        status: 'pending',
        appliedAt: serverTimestamp(),
        bookingDetails: {
          carType: booking.carType,
          pickupLocation: booking.pickupLocation,
          destination: booking.destination,
          date: booking.date,
          hours: booking.hours,
          price: booking.price,
          userEmail: booking.userEmail,
          userPhone: booking.userPhone,
          city: booking.city,
          userId: booking.userId
        }
      };

      console.log('Creating application with data:', applicationData);

      const applicationRef = await addDoc(collection(db, 'applications'), applicationData);
      console.log('Application created successfully:', applicationRef.id);

      // Try to update booking notifications (but don't fail the whole process if this fails)
      try {
        await updateDoc(doc(db, 'bookings', booking.id), {
          'notifications.user': `Driver ${userData.firstName} has applied for your booking. Check your applications to accept.`,
          'notifications.updatedAt': serverTimestamp()
        });
        console.log('Booking notification updated successfully');
      } catch (notificationError) {
        console.warn('Failed to update booking notification (but application was created):', notificationError);
        // Don't throw error here - application was created successfully
      }

      alert('Application submitted successfully! The user will be notified.');

    } catch (error) {
      console.error('Error applying for booking:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        bookingId: booking.id
      });
      
      if (error.code === 'permission-denied') {
        alert('Permission denied. Please check if you are logged in properly.');
      } else if (error.code === 'failed-precondition') {
        alert('Unable to submit application. Please try again.');
      } else {
        alert('Error submitting application. Please try again.');
      }
    } finally {
      setApplyingBookingId(null);
    }
  };

  // Filter available bookings to exclude those the driver has already applied to
  const filteredAvailableBookings = availableBookings.filter(booking => 
    !hasAlreadyApplied(booking.id)
  );

  const statCards = [
    {
      title: 'Total Applications',
      value: driverStats.totalApplications,
      color: 'blue',
      icon: '📝'
    },
    {
      title: 'Accepted Jobs',
      value: driverStats.acceptedJobs,
      color: 'green',
      icon: '✅'
    },
    {
      title: 'Pending Applications',
      value: driverStats.pendingApplications,
      color: 'yellow',
      icon: '⏳'
    }
  ];

  // Show access denied if not driver
  if (!currentUser || userData?.userType !== 'driver') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500">You need to be logged in as a driver to access this page.</p>
          <p className="text-sm text-gray-400 mt-2">
            Your current type: {userData?.userType || 'Not set'}<br />
            Your city: {userData?.city || 'Not set'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading driver dashboard...</p>
          <p className="text-sm text-gray-400">City: {userData?.city}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">
            Driver Dashboard
          </h1>
          <p className="text-gray-600">
            Welcome, {userData?.firstName}! Available bookings in {userData?.city}
          </p>
          <p className="text-sm text-blue-600 mt-1">
            Found {filteredAvailableBookings.length} available bookings
            {filteredAvailableBookings.length !== availableBookings.length && 
              ` (${availableBookings.length - filteredAvailableBookings.length} already applied)`
            }
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <div className="flex items-center">
                <div className="text-3xl mr-4">{stat.icon}</div>
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-lg">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'available', name: 'Available Bookings', count: filteredAvailableBookings.length },
                { id: 'myApplications', name: 'My Applications', count: myApplications.length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'available' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-4">
                  Available Bookings in {userData?.city}
                </h3>
                
                {filteredAvailableBookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6 bg-blue-50"
                  >
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xl font-semibold mb-2">
                          {booking.carType} Booking
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="font-medium">Route:</span> {booking.pickupLocation} → {booking.destination}</p>
                          <p><span className="font-medium">Date & Time:</span> {booking.date?.toDate?.() ? new Date(booking.date.toDate()).toLocaleString() : 'Not specified'}</p>
                          <p><span className="font-medium">Duration:</span> {booking.hours} hours</p>
                          <p><span className="font-medium">User:</span> {booking.userEmail}</p>
                          <p><span className="font-medium">User Phone:</span> {booking.userPhone || 'Not provided'}</p>
                          <p><span className="font-medium">City:</span> {booking.city}</p>
                          <p><span className="font-medium">Status:</span> <span className="text-green-600 font-semibold">{booking.status}</span></p>
                        </div>
                      </div>
                      
                      <div>
                        <div className="bg-white p-4 rounded-lg mb-4">
                          <h5 className="font-semibold mb-2">Pricing Details</h5>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Subtotal:</span>
                              <span>£{booking.price?.subtotal}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>GST (20%):</span>
                              <span>£{booking.price?.tax}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-1">
                              <span>Total:</span>
                              <span>£{booking.price?.total}</span>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleApplyForBooking(booking)}
                          disabled={applyingBookingId === booking.id || hasAlreadyApplied(booking.id)}
                          className={`w-full py-3 rounded-lg transition-colors font-semibold ${
                            applyingBookingId === booking.id 
                              ? 'bg-gray-400 text-white cursor-not-allowed' 
                              : hasAlreadyApplied(booking.id)
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {applyingBookingId === booking.id ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Applying...
                            </>
                          ) : hasAlreadyApplied(booking.id) ? (
                            'Already Applied'
                          ) : (
                            'Apply for this Booking'
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {booking.notifications?.drivers && (
                      <p className="text-sm text-blue-600 mt-3 bg-white p-2 rounded">
                        📢 {booking.notifications.drivers}
                      </p>
                    )}
                  </motion.div>
                ))}
                
                {filteredAvailableBookings.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🚗</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      {availableBookings.length === 0 ? 'No available bookings' : 'No new bookings to apply'}
                    </h3>
                    <p className="text-gray-500">
                      {availableBookings.length === 0 
                        ? `There are currently no approved bookings in ${userData?.city}. Check back later!`
                        : 'You have already applied to all available bookings.'
                      }
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'myApplications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-4">My Job Applications</h3>
                
                {myApplications.map((application, index) => (
                  <motion.div
                    key={application.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6 bg-white"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold">
                          {application.bookingDetails?.carType} - {application.bookingDetails?.pickupLocation} to {application.bookingDetails?.destination}
                        </h4>
                        <p className="text-gray-600">
                          Applied on {application.appliedAt?.toDate?.() ? new Date(application.appliedAt.toDate()).toLocaleDateString() : 'Recent'}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {application.status}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Date:</span> {application.bookingDetails?.date?.toDate?.() ? new Date(application.bookingDetails.date.toDate()).toLocaleString() : 'Not specified'}</p>
                        <p><span className="font-medium">Hours:</span> {application.bookingDetails?.hours}</p>
                        <p><span className="font-medium">Total Pay:</span> £{application.bookingDetails?.price?.total}</p>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">User Email:</span> {application.bookingDetails?.userEmail}</p>
                        <p><span className="font-medium">Your Contact:</span> {application.driverPhone}</p>
                        {application.status === 'accepted' && (
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-green-800 font-semibold">
                              ✅ You've been accepted! Contact the user at: {application.bookingDetails?.userEmail}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {myApplications.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📝</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      No applications yet
                    </h3>
                    <p className="text-gray-500">
                      You haven't applied for any bookings yet. Check the available bookings tab to get started!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDashboard;